import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money } from '../../utils/region';
import { useReadOnlySeat } from '../../utils/readOnlySeat';

// ── Dividing one leg of a draft across several accounts, by hand ────────────────────────────────
// Routing by charge (the sale side) and by supplier (the purchase side) answers the cases that
// REPEAT: the second bill from a supplier, the eleventh invoice carrying the same charge name. This
// is the screen for everything else — and it is where the FIRST answer comes from, because nothing
// in this system infers a classification from a name, a code or a rate. One leg becomes several
// rows and a person decides every one of them.
//
// Two properties this file is held to:
//
// 1. THE SUM IS PINNED. The rows have to come to the leg's own figure to the satang, and the screen
//    says how far off it is at every keystroke. It refuses to send a split that does not add up —
//    but the refusal that MATTERS is the server's, which checks the same thing again before it
//    writes anything. A check that only runs here is not an accounting control: a stale tab, a
//    second window and a replayed request all skip it.
// 2. SATANG, NOT BAHT. Everything below works in whole satang, which is what journal_lines holds.
//    100.00 into three is 33.34 + 33.33 + 33.33, and the row carrying that odd satang is shown
//    saying so rather than left for someone to discover in a trial balance.
//
// ⛔ And the thing it must NOT do: write a mapping. Allocating one document says nothing about the
// next one. `RememberAccount` below is a separate, deliberate act with its own button — a one-off
// allocation that quietly became a standing instruction is the defect this design forbids, because
// nobody would see it happen and the next hundred documents would carry a decision nobody made.
//
// ⛔ Both editors here only write (POST /journal/line/:id/split, POST /journal/remember-account), so a
// read-only seat (a client's own books login, the auditor — utils/readOnlySeat.ts) is never drawn
// either. DraftEntries does not offer that seat the Split or Remember button that opens them; this
// is the same rule held where the Save buttons live, so no other mount can bring them back.

export type SplitLine = { id: string; account_id: string; dr: number; cr: number; code: string; name_th: string; name_en: string };
export type Account = { id: string; code: string; name_th: string; name_en: string; class: string };

const baht = (satang: number) => money(Number(satang || 0) / 100);
/** A typed amount in whole satang, or NaN when it is not an amount. Commas and spaces are allowed
 *  because operators paste figures; a third decimal is not, because the books hold no such thing. */
const toSat = (s: string): number => {
  const t = String(s ?? '').replace(/[\s,]/g, '');
  if (!/^\d+(\.\d{0,2})?$/.test(t)) return NaN;
  return Math.round(parseFloat(t) * 100);
};
const show = (satang: number) => (satang / 100).toFixed(2);

const btn: React.CSSProperties = {
  padding: '5px 10px', fontSize: 11.5, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};
const fld: React.CSSProperties = {
  padding: '4px 7px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)',
};
const accountLabel = (a: Account) => `${a.code} · ${a.name_th} — ${a.name_en}`;

type Row = { key: string; accountId: string; amount: string };
let seq = 0;
const row = (accountId = '', amount = ''): Row => ({ key: `r${++seq}`, accountId, amount });

export default function SplitLeg({ line, accounts, onSaved, onCancel }: {
  line: SplitLine; accounts: Account[]; onSaved: (msg: string) => void; onCancel: () => void;
}) {
  const { t } = useTranslation();
  const readOnly = useReadOnlySeat();
  const leg = Number(line.dr) || Number(line.cr) || 0;
  // Two rows to start: a split is at least two by definition, and the leg's own account is the
  // honest first row — most splits take a piece OFF an account rather than empty it.
  const [rows, setRows] = useState<Row[]>([row(line.account_id), row()]);
  // Which row was given the odd satang, and how much it was: kept as state rather than recomputed,
  // so the badge cannot go on saying "carries 0.01" after the rows it was true of have changed.
  const [odd, setOdd] = useState<{ key: string; satang: number } | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const sats = rows.map(r => (r.amount.trim() === '' ? 0 : toSat(r.amount)));
  const anyBad = sats.some(s => Number.isNaN(s));
  const allocated = sats.reduce((s, v) => s + (Number.isNaN(v) ? 0 : v), 0);
  const remainder = leg - allocated;
  const missingAccount = rows.some(r => !r.accountId);
  const dupe = useMemo(() => {
    const seen = new Set<string>();
    for (const r of rows) { if (r.accountId && seen.has(r.accountId)) return true; seen.add(r.accountId); }
    return false;
  }, [rows]);
  const ready = !anyBad && !missingAccount && !dupe && remainder === 0 && rows.length >= 2 && sats.every(s => s > 0);

  const set = (key: string, patch: Partial<Row>) => {
    setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)));
    if (patch.amount !== undefined && odd && key === odd.key) setOdd(null);
  };
  /** Evenly, in satang, with the odd satang landing on ONE row — named, not hidden. 100.00 into
   *  three cannot be three equal rows, and the alternative to saying which row carries the extra
   *  satang is losing it. */
  const divide = () => {
    const n = rows.length;
    const base = Math.floor(leg / n);
    const extra = leg - base * n;
    setRows(rs => rs.map((r, i) => ({ ...r, amount: show(i === 0 ? base + extra : base) })));
    setOdd(extra ? { key: rows[0].key, satang: extra } : null);
    setErr('');
  };

  const save = async () => {
    if (anyBad) { setErr(t('pa_split_bad_amount', { n: sats.findIndex(s => Number.isNaN(s)) + 1 })); return; }
    if (missingAccount) { setErr(t('pa_split_need_account')); return; }
    if (dupe) { setErr(t('pa_split_dup')); return; }
    if (remainder !== 0) { setErr(remainder > 0 ? t('pa_split_remaining', { amount: baht(remainder) }) : t('pa_split_over', { amount: baht(-remainder) })); return; }
    setBusy(true); setErr('');
    try {
      await api.jnlSplitLeg(line.id, rows.map((r, i) => ({ accountId: r.accountId, satang: sats[i] })));
      onSaved(t('pa_split_saved'));
    } catch (e: any) { setErr(e?.message || ''); }   // the server's own words, unwrapped
    finally { setBusy(false); }
  };

  if (readOnly) return null;
  return (
    <div style={{ border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--bg-app)', padding: '10px 11px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text-primary)' }}>{t('pa_split_head', { amount: baht(leg) })}</span>
        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('pa_split_now_on', { account: `${line.code} ${line.name_en}` })}</span>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
            <th style={{ textAlign: 'left', padding: '0 0 4px', fontWeight: 700 }}>{t('pa_split_account')}</th>
            <th style={{ textAlign: 'right', padding: '0 0 4px', fontWeight: 700, width: 150 }}>{t('pa_split_amount')}</th>
            <th style={{ width: 28 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.key}>
              <td style={{ padding: '3px 6px 3px 0' }}>
                {/* The WHOLE chart, not the classes this leg "ought" to be. Filtering the list would
                    be this screen deciding what the money is, which is the one thing it must not do. */}
                <select value={r.accountId} onChange={e => set(r.key, { accountId: e.target.value })}
                  style={{ ...fld, width: '100%', maxWidth: 420 }}>
                  <option value="">{t('pa_split_choose')}</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{accountLabel(a)}</option>)}
                </select>
              </td>
              <td style={{ padding: '3px 0' }}>
                <input value={r.amount} onChange={e => set(r.key, { amount: e.target.value })} inputMode="decimal"
                  style={{ ...fld, width: '100%', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                           borderColor: Number.isNaN(sats[i]) ? '#F3B9B9' : 'var(--border-strong)' }} />
                {odd && r.key === odd.key && (
                  <div style={{ fontSize: 10.5, color: '#92400E', textAlign: 'right', marginTop: 2 }}>
                    {t('pa_split_carries_odd', { amount: baht(odd.satang) })}
                  </div>
                )}
              </td>
              <td style={{ padding: '3px 0', textAlign: 'right' }}>
                {rows.length > 2 && (
                  <button onClick={() => { setRows(rs => rs.filter(x => x.key !== r.key)); setOdd(null); }} title={t('pa_split_remove_row')}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 13 }}>
                    <i className="ti ti-x" aria-hidden />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        <button onClick={() => { setRows(rs => [...rs, row()]); setOdd(null); }} style={btn}><i className="ti ti-plus" aria-hidden /> {t('pa_split_add_row')}</button>
        <button onClick={divide} style={btn}>{t('pa_split_even')}</button>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                       color: remainder === 0 ? '#166534' : '#92400E' }}>
          {remainder === 0 ? t('pa_split_balanced') : remainder > 0
            ? t('pa_split_remaining', { amount: baht(remainder) })
            : t('pa_split_over', { amount: baht(-remainder) })}
        </span>
      </div>

      {err && <div style={{ fontSize: 12, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9',
                            borderRadius: 6, padding: '6px 9px', marginTop: 8 }}>{err}</div>}

      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.6, marginTop: 8 }}>
        {t('pa_split_pin_why', { amount: baht(leg) })} {t('pa_split_draft_only')}
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 9 }}>
        <button onClick={onCancel} style={btn}>{t('pa_split_cancel')}</button>
        <button onClick={save} disabled={!ready || busy}
          style={{ ...btn, background: ready ? 'var(--accent)' : 'var(--bg-surface)', color: ready ? '#fff' : 'var(--text-tertiary)',
                   border: ready ? 'none' : '1px solid var(--border-strong)', opacity: busy ? 0.6 : 1,
                   cursor: ready && !busy ? 'pointer' : 'not-allowed' }}>
          <i className={busy ? 'ti ti-loader' : 'ti ti-arrows-split-2'} style={{ display: 'inline-block', animation: busy ? 'spin 0.7s linear infinite' : undefined }} aria-hidden />
          {' '}{t('pa_split_save')}
        </button>
      </div>
    </div>
  );
}

// ── "Remember this for next time" ───────────────────────────────────────────────────────────────
// The separate act. It writes the per-charge mapping the posting engine reads — on the sale side
// into the revenue memory, on the purchase side into its counterpart — and it is keyed on the
// charge AS THE DOCUMENT SPELLS IT, offered from the document's own items rather than retyped from
// memory, because a mapping keyed on a typo is a mapping that never matches anything again.
type Charge = { chargeCode: string; chargeKey: string; taxClass: string; amount: number };

export function RememberAccount({ entryId, clientId, side, account, onClose }: {
  entryId: string; clientId: string; side: 'sale' | 'purchase'; account: { code: string; name_en: string } | null; onClose: () => void;
}) {
  const { t } = useTranslation();
  const readOnly = useReadOnlySeat();
  const [charges, setCharges] = useState<Charge[] | null>(null);
  const [party, setParty] = useState('');
  const [pick, setPick] = useState('');
  const [typed, setTyped] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (readOnly) return;   // nothing is drawn for that seat (below), so there is nothing to read for
    api.jnlDraftCharges(entryId)
      .then((d: any) => { setCharges(d.items || []); setParty(String(d.party?.name || '')); })
      .catch((e: any) => { setCharges([]); setErr(e?.message || ''); });
  }, [entryId, readOnly]);

  const label = pick === '__other' || !pick ? typed.trim() : pick;
  const save = async () => {
    if (!account) return;
    if (!label) { setErr(t('pa_rem_need_label')); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      await api.jnlRememberAccount({ clientId, side, scope: 'client', label, accountCode: account.code });
      setMsg(t('pa_rem_saved', { label, account: `${account.code} ${account.name_en}` }));
    } catch (e: any) { setErr(e?.message || ''); }   // including "there is nowhere to remember it yet"
    finally { setBusy(false); }
  };

  if (!account || readOnly) return null;
  return (
    <div style={{ border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--bg-app)', padding: '10px 11px' }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>
        {t('pa_rem_head', { account: `${account.code} ${account.name_en}` })}
      </div>
      {!!party && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 6 }}>{t('pa_rem_party', { name: party })}</div>}

      <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginBottom: 4 }}>{t('pa_rem_which')}</div>
      {charges && charges.length > 0 ? (
        <select value={pick} onChange={e => setPick(e.target.value)} style={{ ...fld, width: '100%', maxWidth: 420 }}>
          <option value="">{t('pa_split_choose')}</option>
          {charges.map(c => <option key={c.chargeKey} value={c.chargeCode}>{c.chargeCode} · {money(c.amount)}</option>)}
          <option value="__other">{t('pa_rem_other')}</option>
        </select>
      ) : (
        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 4 }}>{t('pa_rem_none')}</div>
      )}
      {(!charges || !charges.length || pick === '__other') && (
        <input value={typed} onChange={e => setTyped(e.target.value)} placeholder={t('pa_rem_type')}
          style={{ ...fld, width: '100%', maxWidth: 420, marginTop: 5 }} />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {/* ⛔ THIS CLIENT, always — there is no firm-wide choice here any more (owner's rule of
            2026-10-15: a company's services name that company's own chart, and nothing else).
            The picker that offered it was also the one thing in the app that could CREATE a
            firm-scoped mapping, while the four mapping cards that could remove one were taken off
            the Chart of accounts page in phase 6 — so a default chosen by accident, for every
            client at once, could not be taken back from any screen. Firm rows written before today
            still route exactly as they did, and Sales › Products & accounts shows them beside the
            code they answer for; this door simply does not make new ones. */}
        <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_rem_scope_client_only')}</span>
        <span style={{ flex: 1 }} />
        <button onClick={onClose} style={btn}>{t('pa_rem_cancel')}</button>
        <button onClick={save} disabled={busy || !label}
          style={{ ...btn, background: label ? 'var(--accent)' : 'var(--bg-surface)', color: label ? '#fff' : 'var(--text-tertiary)',
                   border: label ? 'none' : '1px solid var(--border-strong)', opacity: busy ? 0.6 : 1 }}>
          <i className={busy ? 'ti ti-loader' : 'ti ti-bookmark'} aria-hidden /> {t('pa_rem_save')}
        </button>
      </div>

      {msg && <div style={{ fontSize: 12, color: '#166534', background: '#E8F6EE', border: '1px solid #BBE5CB',
                            borderRadius: 6, padding: '6px 9px', marginTop: 8 }}>{msg}</div>}
      {err && <div style={{ fontSize: 12, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9',
                            borderRadius: 6, padding: '6px 9px', marginTop: 8 }}>{err}</div>}
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.6, marginTop: 8 }}>{t('pa_rem_why')}</div>
    </div>
  );
}
